/**
 * 正誤判定と単元スコア(星3段階)。
 *
 * **コインを実際に加算するのは Cloud Functions だけ**(CLAUDE.md ルール2)。
 * ここにあるのはクライアント表示用の計算と、将来のサーバー実装が突き合わせる基準。
 * しきい値は `config.ts` からのみ読む。
 */

import { QUIZ_STARS } from './config.js';
import type { QuizQuestion } from './types.js';

/** 正解の選択肢の添字。見つからなければ `-1`(スキーマ検証を通っていれば必ず1つある)。 */
export function correctChoiceIndex(question: QuizQuestion): number {
  return question.choices.findIndex((choice) => choice.correct === true);
}

/** その添字の選択肢が正解か。 */
export function isCorrectChoice(question: QuizQuestion, index: number): boolean {
  return question.choices[index]?.correct === true;
}

/** 1問ぶんの回答記録。 */
export interface QuizAnswerEntry {
  readonly questionId: string;
  readonly correct: boolean;
  /** その問題にかかった時間(ms)。学習イベント(docs/07)へ流す。 */
  readonly elapsedMs?: number;
}

/** 単元1回ぶんの成績。 */
export interface QuizUnitScore {
  /** といた問題数。 */
  readonly total: number;
  /** せいかいした数。 */
  readonly correct: number;
  /** 正答率(0〜1)。1問も解いていなければ 0。 */
  readonly ratio: number;
  /** 星(0〜3)。3=全問正解 / 2=8割 / 1=クリア / 0=もういちど。 */
  readonly stars: number;
  /** 星1以上か(単元クリア)。 */
  readonly cleared: boolean;
  /** かかった時間の合計(ms)。 */
  readonly totalMs: number;
  /** いちばん長かった連続正解。 */
  readonly bestStreak: number;
}

/**
 * 正答率から星を決める(docs/10 §2 の3段階)。
 * 1問も解いていないときは 0。
 */
export function quizStars(correct: number, total: number): number {
  if (total <= 0) return 0;
  const ratio = Math.max(0, Math.min(correct, total)) / total;
  if (ratio >= QUIZ_STARS.full) return 3;
  if (ratio >= QUIZ_STARS.good) return 2;
  if (ratio >= QUIZ_STARS.clear) return 1;
  return 0;
}

/** 単元1回ぶんの回答記録を集計する。 */
export function scoreQuizUnit(entries: readonly QuizAnswerEntry[]): QuizUnitScore {
  let correct = 0;
  let totalMs = 0;
  let streak = 0;
  let bestStreak = 0;
  for (const entry of entries) {
    totalMs += Math.max(0, entry.elapsedMs ?? 0);
    if (entry.correct) {
      correct += 1;
      streak += 1;
      if (streak > bestStreak) bestStreak = streak;
    } else {
      streak = 0;
    }
  }
  const total = entries.length;
  const stars = quizStars(correct, total);
  return {
    total,
    correct,
    ratio: total === 0 ? 0 : correct / total,
    stars,
    cleared: stars > 0,
    totalMs,
    bestStreak,
  };
}
